import { LitElement, html } from 'lit';
import { Controllers } from '@vitesse/proto/vitesse/v1/controller_pb.js';
import '@vitesse/base-ui';
import { session } from '../../../core/session';
import { customElement, property } from 'lit/decorators.js';
import type { BackendClient } from '../../bridge/backend-client';
import type { ControllerLog } from '../../../core/bridge/connect-client';
import { type LogLine } from '../log-tree';
import '../log-tree';

/*
 * <vitesse-controller-logs> — the controller Logs tab. Thin wrapper that points
 * the shared <vitesse-log-tree> at this controller's stream
 * (Controllers.SubscribeLogs: recent history replay + live tail) and maps each
 * ControllerLog to the uniform LogLine (title + optional body).
 * The element is only mounted while the Logs tab is open, so the upstream NATS
 * interest exists only while someone is watching.
 *
 * TRANSITIONAL mapping: until producers emit the standardized LogEvent
 * (title/body/category/outcome), we derive them from today's wire — the message
 * becomes the `title`, the slog attrs become a plain-text `body`, and `ok=true`
 * becomes `outcome:success` (the green badge). Once the producers adopt the new
 * schema this collapses to a pass-through.
 */
@customElement('vitesse-controller-logs')
export class ControllerLogsSection extends LitElement {
  @property({ attribute: false }) backend?: BackendClient;
  @property() gatewayId = '';
  @property() controllerId = '';

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM → shared `.lt-*` agent-styles apply
  }

  /** ControllerLog → the uniform LogLine (severity + title + body): the message
   *  is the `title`, `body` is the expanded detail.
   *
   *  Producers on the standardized schema emit `body` directly. For any producer
   *  that hasn't adopted it yet (legacy plugins still emitting only slog attrs),
   *  fall back to synthesizing a plain-text body from the attrs — `addr` dropped
   *  (transport hint, lives in Properties) and `ok` dropped (the old green flag;
   *  severity is now just info / warning / error). Black-box by construction. */
  private static toLine(item: unknown): LogLine {
    const l = item as ControllerLog;
    let body = l.body || undefined;
    if (!body) {
      const parts: string[] = [];
      for (const [k, v] of Object.entries(l.attrs)) {
        if (k === 'addr' || k === 'ok') continue;
        const [label, value] = fmtAttr(k, v);
        parts.push(`${label} ${value}`);
      }
      body = parts.length ? parts.join(' · ') : undefined;
    }
    return { tsMs: l.tsMs, severity: l.severity, title: l.message, body };
  }

  // STABLE connect — a fresh arrow each render would change the `.connect`
  // binding and force the whole <vitesse-log-tree> to re-render (re-evaluating
  // its entire row list) on EVERY parent render. Reads backend/ids at call time;
  // the log-tree re-subscribes only when subscriptionKey changes. (Same fix as
  // vitesse-model-detail — a fast-re-rendering parent otherwise pegs the page
  // once the log list grows.)
  private readonly connectLogs = (): Promise<MessagePort> =>
    this.backend!.api.subscribeControllerLogs(this.gatewayId, this.controllerId);

  render() {
    const ready = !!(this.backend && this.controllerId);
    return html`<vitesse-gated
      .session=${session} .rpc=${Controllers.method.subscribeLogs}
    >
      <vitesse-log-tree
      .subscriptionKey=${ready ? this.controllerId : ''}
      .connect=${ready ? this.connectLogs : undefined}
      .toLine=${ControllerLogsSection.toLine}
    ></vitesse-log-tree>
    </vitesse-gated>`;
  }
}

/** key=value → a readable {label, value}: unit suffixes move onto the value and
 *  out of the label (rtt_ms=9 → rtt · 9ms; values_per_sec=252 → values · 252/s). */
function fmtAttr(k: string, v: string): [label: string, value: string] {
  const units: [string, string][] = [
    ['_per_sec', '/s'],
    ['_ms', 'ms'],
    ['_pct', '%'],
    ['_s', 's'],
  ];
  for (const [suf, unit] of units) {
    if (k.endsWith(suf)) return [k.slice(0, -suf.length).replace(/_/g, ' '), v + unit];
  }
  return [k.replace(/_/g, ' '), v];
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-controller-logs': ControllerLogsSection;
  }
}
